// Amos-83 Request Portal — entry → check (validate, read-only) → review → submit → done.
// Amos-104: no "Choose periods" (G2, always all periods); per-ref repeat confirm on review; Not-found shown
// once, on the done (confirmation) screen; "Also send to" (G3).
import React, { useState } from 'react';
import { COPY } from './copy';
import { portalApi, PortalApiError, Me, ValidateResult, CreateResult, newSubmissionId } from './api';
import { fmtDay } from './StatusTable';

type Mode = 'single' | 'paste' | 'upload';
type Step = 'entry' | 'checking' | 'review' | 'submitting' | 'done';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function splitEmails(s: string): string[] {
  return s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
}

export function apiErrorMessage(e: unknown, maxRefs: number): string {
  const err = e as PortalApiError;
  if (!(err instanceof PortalApiError)) return COPY.errGeneric;
  if (err.status === 0) return COPY.errNetwork;
  if (err.status === 401) return COPY.sessionExpired;
  const d = typeof err.detail === 'string' ? err.detail : '';
  if (/No policy references/i.test(d)) return COPY.errNoRefs;
  if (/Too many/i.test(d)) return COPY.errTooMany(maxRefs);
  if (/Unsupported file type/i.test(d)) return COPY.errBadFile;
  const m = /Invalid deliver-to address: (.+)$/.exec(d);
  if (m) return COPY.errBadEmail(m[1]);
  if (err.status >= 400 && err.status < 500 && d) return d;
  return COPY.errGeneric;
}

interface Props { me: Me; onSubmitted: () => void; onViewMine: () => void; onSessionExpired: () => void }

export default function RequestForm({ me, onSubmitted, onViewMine, onSessionExpired }: Props) {
  const [mode, setMode] = useState<Mode>('single');
  const [step, setStep] = useState<Step>('entry');
  const [single, setSingle] = useState('');
  const [paste, setPaste] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [deliverTo, setDeliverTo] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [fieldError, setFieldError] = useState<{ deliverTo?: string; input?: string }>({});
  const [result, setResult] = useState<ValidateResult | null>(null);
  const [repeatOk, setRepeatOk] = useState<Record<string, boolean>>({});
  const [submissionId, setSubmissionId] = useState<string>(newSubmissionId());
  const [done, setDone] = useState<CreateResult | null>(null);

  const reset = () => {
    setStep('entry'); setSingle(''); setPaste(''); setFile(null); setDeliverTo(''); setNote('');
    setError(''); setFieldError({}); setResult(null); setRepeatOk({}); setDone(null); setSubmissionId(newSubmissionId());
  };

  const localCheck = (): boolean => {
    const fe: typeof fieldError = {};
    if (mode === 'single' && !single.trim()) fe.input = COPY.errNoRefs;
    if (mode === 'paste' && !paste.trim()) fe.input = COPY.errNoRefs;
    if (mode === 'upload' && !file) fe.input = COPY.uploadLabel;
    if (mode === 'upload' && file && !/\.(xlsx|xlsm|csv)$/i.test(file.name)) fe.input = COPY.errBadFile;
    const bad = splitEmails(deliverTo).find((a) => !EMAIL_RE.test(a));
    if (bad) fe.deliverTo = bad.includes('@') ? COPY.errBadEmail(bad) : COPY.errDeliverToMissingAt;
    setFieldError(fe);
    return Object.keys(fe).length === 0;
  };

  const check = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!localCheck()) return;
    setStep('checking');
    try {
      const r = mode === 'upload'
        ? await portalApi.validateUpload(file as File)
        : await portalApi.validate(mode === 'single' ? { refs: [single.trim()] } : { text: paste });
      setResult(r);
      setRepeatOk({});
      setStep('review');
    } catch (err) {
      if ((err as PortalApiError).status === 401) { onSessionExpired(); return; }
      setError(apiErrorMessage(err, me.max_refs));
      setStep('entry');
    }
  };

  const submit = async () => {
    if (!result) return;
    setError('');
    setStep('submitting');
    const deliver = splitEmails(deliverTo);
    try {
      const r = mode === 'upload'
        ? await portalApi.createUpload(file as File, { submission_id: submissionId, note: note || undefined, deliver_to: deliver.join(',') || undefined })
        : await portalApi.create({
            submission_id: submissionId,
            ...(mode === 'single' ? { refs: [single.trim()] } : { text: paste }),
            period: 'all',
            note: note || undefined,
            deliver_to: deliver.length ? deliver : undefined,
          });
      setDone(r);
      setStep('done');
      onSubmitted();
    } catch (err) {
      if ((err as PortalApiError).status === 401) { onSessionExpired(); return; }
      setError(apiErrorMessage(err, me.max_refs));
      setStep('review');
    }
  };

  const input = 'w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
  const label = 'block text-sm font-medium text-slate-700 mb-1';
  const hint = 'text-xs text-slate-500 mt-1';

  // ── done ──
  if (step === 'done' && done) {
    const n = done.created.length;
    return (
      <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6" data-testid="portal-done">
        <h2 className="text-lg font-semibold text-slate-800">{COPY.doneHeading}</h2>
        <p className="text-sm text-slate-700 mt-1">{n > 0 ? COPY.doneIntro(n) : COPY.reviewNothing}</p>
        {done.replayed && <p className="text-xs text-amber-700 mt-1">{COPY.doneReplayed}</p>}
        {n > 0 && (
          <ul className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded-md">
            {done.created.map((c) => (
              <li key={c.request_id} className="px-3 py-2 text-sm flex justify-between gap-4">
                <span className="font-mono text-slate-800">{c.ref}</span>
                <span className="text-slate-600 truncate">{c.insured_name}</span>
              </li>
            ))}
          </ul>
        )}
        {n > 0 && <p className="text-sm text-slate-600 mt-4">{COPY.doneWhatNext}</p>}
        {(done.unvalidated.length + done.rejected_shape.length) > 0 && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3 mt-4" data-testid="portal-done-not-found">
            <p>
              {COPY.doneNotFoundReminder(done.unvalidated.length + done.rejected_shape.length)}{' '}
              <span className="font-mono">{[...done.unvalidated, ...done.rejected_shape].join(', ')}</span>
            </p>
            <p className="text-xs mt-1">{COPY.doneNotFoundIntro}</p>
          </div>
        )}
        {done.failed.length > 0 && (
          <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mt-4">
            {COPY.doneFailed(done.failed.length)} <span className="font-mono">{done.failed.map((f) => f.ref).join(', ')}</span>
          </p>
        )}
        <div className="flex gap-3 mt-6">
          <button onClick={reset} className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">{COPY.doneAgain}</button>
          <button onClick={onViewMine} className="py-2 px-4 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 text-sm">{COPY.doneViewMine}</button>
        </div>
      </section>
    );
  }

  // ── review ──
  if ((step === 'review' || step === 'submitting') && result) {
    const n = result.validated.length;
    const repeatsPending = result.validated.some((v) => v.last_requested_at && !repeatOk[v.ref]);
    return (
      <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6" data-testid="portal-review">
        <h2 className="text-lg font-semibold text-slate-800">{COPY.reviewHeading}</h2>
        {error && <div role="alert" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-md p-3 mt-3">{error}</div>}

        <h3 className="text-sm font-semibold text-slate-700 mt-4">{COPY.reviewFound(n)}</h3>
        {n === 0 ? (
          <p className="text-sm text-slate-600 mt-1">{COPY.reviewNothing}</p>
        ) : (
          <>
            <p className={hint}>{COPY.reviewFoundIntro}</p>
            <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded-md">
              {result.validated.map((v) => (
                <li key={v.ref} className="px-3 py-3 text-sm">
                  <div className="flex justify-between gap-4">
                    <span className="font-mono text-slate-800">{v.ref}</span>
                    <span className="text-slate-700 truncate">{v.insured_name}</span>
                  </div>
                  {v.last_requested_at && (
                    <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`portal-repeat-${v.ref}`}>
                      <p>{COPY.repeatWarning(fmtDay(v.last_requested_at))}</p>
                      <label className="inline-flex items-center gap-1 mt-1">
                        <input type="checkbox" checked={!!repeatOk[v.ref]} onChange={(e) => setRepeatOk((m) => ({ ...m, [v.ref]: e.target.checked }))} />
                        <span>{COPY.repeatConfirm}</span>
                      </label>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        {n === 0 && result.unvalidated.length > 0 && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="portal-not-found">
            <h3 className="text-sm font-semibold text-amber-800">{COPY.reviewNotFound(result.unvalidated.length)}</h3>
            <p className="text-xs text-amber-800 mt-1">{COPY.reviewNotFoundIntro}</p>
            <p className="font-mono text-sm text-amber-900 mt-2">{result.unvalidated.join(', ')}</p>
          </div>
        )}
        {result.rejected_shape.length > 0 && (
          <div className="mt-3 bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="portal-bad-shape">
            <h3 className="text-sm font-semibold text-slate-700">{COPY.reviewBadShape(result.rejected_shape.length)}</h3>
            <p className="text-xs text-slate-600 mt-1">{COPY.reviewBadShapeIntro}</p>
            <p className="font-mono text-sm text-slate-800 mt-2">{result.rejected_shape.join(', ')}</p>
          </div>
        )}

        {n > 0 && repeatsPending && <p className="text-xs text-amber-800 mt-4" data-testid="portal-repeat-pending">{COPY.repeatPending}</p>}
        <div className="flex gap-3 mt-6">
          <button onClick={submit} disabled={n === 0 || repeatsPending || step === 'submitting'} data-testid="portal-submit"
            className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-sm">
            {step === 'submitting' ? COPY.submitting : COPY.submitButton(n)}
          </button>
          <button onClick={() => { setStep('entry'); setError(''); }} disabled={step === 'submitting'}
            className="py-2 px-4 border border-slate-300 text-slate-700 rounded-md hover:bg-slate-50 text-sm">{COPY.backButton}</button>
        </div>
      </section>
    );
  }

  // ── entry / checking ──
  const busy = step === 'checking';
  return (
    <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6" data-testid="portal-entry">
      <h2 className="text-lg font-semibold text-slate-800">{COPY.formHeading}</h2>
      <p className="text-sm text-slate-600 mt-1">{COPY.formIntro}</p>
      <form onSubmit={check} className="mt-5 space-y-5">
        <div className="flex gap-2 flex-wrap" role="radiogroup" aria-label="Input mode">
          {([['single', COPY.modeSingle], ['paste', COPY.modePaste], ['upload', COPY.modeUpload]] as const).map(([k, l]) => (
            <button type="button" key={k} onClick={() => { setMode(k); setFieldError({}); }} aria-pressed={mode === k} data-testid={`portal-mode-${k}`}
              className={`px-3 py-1.5 rounded-md text-sm border ${mode === k ? 'bg-blue-50 border-blue-300 text-blue-800' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
              {l}
            </button>
          ))}
        </div>

        {mode === 'single' && (
          <div>
            <label htmlFor="p-single" className={label}>{COPY.singleLabel}</label>
            <input id="p-single" value={single} onChange={(e) => setSingle(e.target.value.toUpperCase())} placeholder={COPY.singlePlaceholder} className={`${input} font-mono`} disabled={busy} />
          </div>
        )}
        {mode === 'paste' && (
          <div>
            <label htmlFor="p-paste" className={label}>{COPY.pasteLabel}</label>
            <textarea id="p-paste" value={paste} onChange={(e) => setPaste(e.target.value)} placeholder={COPY.pastePlaceholder} rows={6} className={`${input} font-mono`} disabled={busy} />
          </div>
        )}
        {mode === 'upload' && (
          <div>
            <label htmlFor="p-file" className={label}>{COPY.uploadLabel}</label>
            <input id="p-file" type="file" accept=".xlsx,.xlsm,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" disabled={busy} />
            <p className={hint}>{COPY.uploadHint}</p>
          </div>
        )}
        {fieldError.input && <p role="alert" className="text-sm text-red-700 -mt-3">{fieldError.input}</p>}

        <div>
          <label htmlFor="p-deliver" className={label}>{COPY.deliverToLabel}</label>
          <input id="p-deliver" value={deliverTo} onChange={(e) => setDeliverTo(e.target.value)} placeholder={COPY.deliverToPlaceholder} className={input} disabled={busy} />
          <p className={hint}>{COPY.deliverToHint(me.backstop_cc)}</p>
          {fieldError.deliverTo && <p role="alert" className="text-sm text-red-700 mt-1">{fieldError.deliverTo}</p>}
        </div>

        <div>
          <label htmlFor="p-note" className={label}>{COPY.noteLabel}</label>
          <textarea id="p-note" value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} placeholder={COPY.notePlaceholder} rows={2} className={input} disabled={busy} />
        </div>

        {error && <div role="alert" className="text-red-700 text-sm bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}

        <button type="submit" disabled={busy} data-testid="portal-check"
          className="py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-sm">
          {busy ? COPY.checking : COPY.checkButton}
        </button>
      </form>
    </section>
  );
}
